import React, { useEffect, useState, useRef } from "react"
import 'ol/ol.css';
import LayerMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import XYZ from "ol/source/XYZ";
import { fromLonLat, toLonLat } from "ol/proj";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Style from "ol/style/Style";
import Icon from "ol/style/Icon";
import Overlay from "ol/Overlay";
import { createPortal } from "react-dom";
import redMarker from "assets/images/marker_red.png"
import greenMarker from "assets/images/marker_green.png"
import Loading from "pages/Custom/Loading";

const VWorld = ({ coordinateList, openRoadView, isLoading }) => {
  const mapRef = useRef(null)
  const overlayRef = useRef(null);
  const mapInstanceRef = useRef(null)
  const geometryRef = useRef(null)
  const vectorLayerRef = useRef(null); //맵 dom 저장용(마커 다시 그릴 때 맵 변경 없이 마커만 렌더링)

  const [boxContent, setBoxContent] = useState(null);

  const makeFeatures = () => {
    return coordinateList.map((item, idx) => {
      const feature = new Feature({
        geometry: new Point(fromLonLat(item.coordinate)),
        content: {
          companyName: item.companyName,
          address: item.address,
          collectionDate: item.collectionDate,
        }
      });
      feature.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: item.state === 'Y' ? redMarker : greenMarker,
            scale: 0.5
          }),
          zIndex: item.state === 'Y' ? 10 : 20
        })
      );
      return feature;
    });
  }

  const makeMapLayer = () => {
    const baseLayer = new TileLayer({
      source: new XYZ({
        // url: "https://api.vworld.kr/req/wmts/1.0.0/02E023B5-5ADD-35A7-93F7-D2C17986D9B7/Base/{z}/{y}/{x}.png",
        url: "https://api.vworld.kr/req/wmts/1.0.0/9394D6DA-912D-3B36-87E3-9C7219A837A2/Base/{z}/{y}/{x}.png",
      }),
    });
    // const coordinateList = [
    //   [126.9780, 37.5665], // 서울 시청
    //   [127.0276, 37.4979], // 강남역
    //   [126.9905, 37.5502], // 명동
    //   [126.9295, 37.5194], // 여의도
    //   [127.1046, 37.3595], // 판교
    // ];

    const features = makeFeatures()

    const vectorSource = new VectorSource({
      features: features,
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    });

    return [baseLayer, vectorLayer]
  }

  const adjustPosition = (zoom) => {
    // 축적에 비례하여 박스위치 조절(마커보다 조금 위에 위치하도록)
    return 2.5 * 2 ** (20 - Math.floor(zoom * 2) / 2)
  }

  const handleZoomChange = () => {
    if (overlayRef.current && geometryRef.current) {
      const view = mapInstanceRef.current?.getView();
      const zoom = view?.getZoom();
      const geometry = [geometryRef.current[0], geometryRef.current[1] + adjustPosition(zoom)]
      overlayRef.current?.setPosition(geometry)
    }
  };

  const sendGeometry = () => {
    openRoadView(toLonLat(geometryRef.current))
  }

  const deleteOverlay = () => {
    setBoxContent(null);
    overlayRef.current?.setPosition(undefined);
  }

  useEffect(() => {
    const overlayContainer = document.createElement("div");
    overlayContainer.setAttribute("id", "box");
    overlayRef.current = new Overlay({
      element: overlayContainer,
      positioning: "bottom-center",
      stopEvent: false,
      offset: [0, -20],
    });

    const [baseLayer, vectorLayer] = makeMapLayer();
    vectorLayerRef.current = vectorLayer;

    const map = new LayerMap({
      target: mapRef.current,
      layers: [baseLayer, vectorLayer],
      view: new View({
        center: fromLonLat([127.7780, 36.9665]),
        zoom: 8,
      }),
      overlays: [overlayRef.current],
    });

    map.on("click", (event) => {
      let found = false;

      map.forEachFeatureAtPixel(event.pixel, (feature) => {
        found = true;
        const coords = event.coordinate;
        const zoom = map.getView().getZoom()
        const flatCoordinates = feature.get('geometry')?.flatCoordinates
        geometryRef.current = flatCoordinates
        const geometry = [flatCoordinates[0], flatCoordinates[1] + adjustPosition(zoom)]
        setBoxContent(feature.get('content'));
        overlayRef.current?.setPosition(geometry || coords);
      });

      // if (!found) {
      //   setBoxContent(null);
      //   overlayRef.current?.setPosition(undefined);
      // }
    });

    mapInstanceRef.current = map
    const view = map.getView()
    if (view) {
      view.on("change:resolution", handleZoomChange);
    }

    return () => {
      map.setTarget(undefined)
      if (view) {
        view.un("change:resolution", handleZoomChange);
      }
    }
  }, []);

  useEffect(() => {
    if (!vectorLayerRef.current) return;

    const features = makeFeatures()

    vectorLayerRef.current.getSource().clear();
    vectorLayerRef.current.getSource().addFeatures(features);
  }, [coordinateList]);

  return (
    <React.Fragment>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      {boxContent &&
        overlayRef.current &&
        createPortal(
          <div
            style={{
              background: "white",
              border: "1px solid gray",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "14px",
              boxShadow: "0 1px 6px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <div className="d-flex w-100 justify-content-between">
              <p className="mb-1">{boxContent.companyName}</p>
              <p className="font-size-14 pointer mb-1" onClick={() => { deleteOverlay() }}>X</p>
            </div>
            <div className="d-flex w-100">
              <p className="mb-1">{boxContent.address}</p>
            </div>
            <div className="d-flex w-100">
              <p className="mb-1">{boxContent.collectionDate}</p>
            </div>
            <div className="mt-1 mb-1">
              <button type="button" className="waves-effect custom-btn active btn btn-sm w-100" onClick={() => {
                sendGeometry()
              }}>
                로드뷰 보기
              </button>
            </div>
          </div>,
          overlayRef.current.getElement()
        )
      }
      {isLoading && <Loading />}
    </React.Fragment>
  )
}

export default VWorld